import * as React from "react";
import { Combobox, Option, Spinner, makeStyles, mergeClasses } from "@fluentui/react-components";

import type { MatterListItem } from "./models";

function matterLabel(matter: MatterListItem): string {
  return `${matter.ref} — ${matter.client}`;
}

const DEFAULT_OPTION_VALUE = "__default__";

function toOptionValue(matterId: number | null): string {
  return matterId === null ? DEFAULT_OPTION_VALUE : String(matterId);
}

function fromOptionValue(value: string): number | null {
  return value === DEFAULT_OPTION_VALUE ? null : Number(value);
}

interface MatterOption {
  value: string;
  label: string;
}

interface MatterComboboxProps {
  matters: MatterListItem[];
  /** null = no matter picked (or, when `defaultOptionLabel` is set, "use the default matter"). */
  selectedMatterId: number | null;
  onSelect: (matterId: number | null) => void;
  placeholder: string;
  /** Presence adds a first "use the default" option, for per-attachment overrides. */
  defaultOptionLabel?: string;
  disabled?: boolean;
  /** Shows a spinner in place of the expand icon and disables interaction, same as `[loading]` on
   *  p-select in nomoros-webapp — the field itself stays put instead of being swapped out. */
  loading?: boolean;
  size?: "small" | "medium" | "large";
  className?: string;
}

const useStyles = makeStyles({
  root: {
    width: "100%",
    minWidth: 0,
  },
  optionText: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

const MatterCombobox: React.FC<MatterComboboxProps> = ({
  matters,
  selectedMatterId,
  onSelect,
  placeholder,
  defaultOptionLabel,
  disabled,
  loading,
  size,
  className,
}) => {
  const styles = useStyles();

  const selectedLabel = React.useMemo(() => {
    if (selectedMatterId === null) {
      return defaultOptionLabel ?? "";
    }
    const matter = matters.find((m) => m.id === selectedMatterId);
    return matter ? matterLabel(matter) : "";
  }, [matters, selectedMatterId, defaultOptionLabel]);

  const [query, setQuery] = React.useState(selectedLabel);

  // Keep the displayed text in sync when the selection changes from outside (e.g. matters finish loading).
  React.useEffect(() => {
    setQuery(selectedLabel);
  }, [selectedLabel]);

  const filteredMatters = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery || normalizedQuery === selectedLabel.toLowerCase()) {
      return matters;
    }
    return matters.filter((matter) => matterLabel(matter).toLowerCase().includes(normalizedQuery));
  }, [matters, query, selectedLabel]);

  const options = React.useMemo<MatterOption[]>(() => {
    const result: MatterOption[] = [];
    if (defaultOptionLabel) {
      result.push({ value: toOptionValue(null), label: defaultOptionLabel });
    }
    filteredMatters.forEach((matter) => {
      result.push({ value: toOptionValue(matter.id), label: matterLabel(matter) });
    });
    return result;
  }, [defaultOptionLabel, filteredMatters]);

  const emptyStateLabel = matters.length === 0 ? "No matters available" : "No matches";

  return (
    <Combobox
      className={mergeClasses(styles.root, className)}
      freeform
      size={size}
      placeholder={loading ? "Loading..." : placeholder}
      value={query}
      disabled={disabled || loading}
      expandIcon={loading ? <Spinner size="extra-tiny" /> : undefined}
      // Caps the popup at the input's own width — without this, long matter names can render a
      // popup wider than the input (and, on the docked ~320px pane, wider than the pane itself).
      positioning={{ matchTargetSize: "width" }}
      onChange={(event) => setQuery(event.target.value)}
      onOptionSelect={(_, data) => {
        onSelect(data.optionValue ? fromOptionValue(data.optionValue) : null);
      }}
      onBlur={() => setQuery(selectedLabel)}
    >
      {options.map((option) => (
        <Option key={option.value} value={option.value} text={option.label}>
          <span className={styles.optionText}>{option.label}</span>
        </Option>
      ))}

      {options.length === 0 && (
        <Option value="__none__" disabled text={emptyStateLabel}>
          {emptyStateLabel}
        </Option>
      )}
    </Combobox>
  );
};

export default MatterCombobox;
